import { useCallback, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useIsLoggedIn } from '@renderer/hooks/useIsLoggedIn'
import { SidepanelIncognitoButton } from '@renderer/overlay/Sidepanel/components/sidepanel/sidepanel-incognito-button'
import { useIsMiniContentHovered, useIsMiniHovered, useIsMiniMenuOpen, useSetMiniOpenSurface } from '../../../atoms'
import { useIsMiniAnchoredLeft } from '../side-context'
import { MINI_REVEAL_TRANSITION, miniPresenceAnchor, miniRevealVariants } from '../side-motion'
import { MiniNewMeetingButton } from './new-meeting-button'
import { MiniNewReminderButton } from './new-reminder-button'

const MINI_INCOGNITO_SURFACE_ID = 'mini-incognito'

function MiniIncognitoButton() {
  const setOpenSurface = useSetMiniOpenSurface()

  const handleOpenChange = useCallback(
    (open: boolean) => setOpenSurface({ id: MINI_INCOGNITO_SURFACE_ID, open }),
    [setOpenSurface],
  )

  useEffect(() => {
    return () => setOpenSurface({ id: MINI_INCOGNITO_SURFACE_ID, open: false })
  }, [setOpenSurface])

  return (
    <SidepanelIncognitoButton
      source="mini-pill"
      tooltipPosition="top"
      className="size-6 rounded-[var(--radius-size-sm)] [&_svg]:!size-4"
      onOpenChange={handleOpenChange}
    />
  )
}

/** Meeting, reminder and incognito shortcuts, shown alongside the menu while the pill is expanded. */
export function MiniQuickActions() {
  const isLoggedIn = useIsLoggedIn()
  const isOnLeftSide = useIsMiniAnchoredLeft()
  const isContentHovered = useIsMiniContentHovered()
  const isMiniHovered = useIsMiniHovered()
  const isMenuOpen = useIsMiniMenuOpen()
  const show = isLoggedIn && (isMenuOpen || (isContentHovered && isMiniHovered))

  return (
    <AnimatePresence initial={false} mode="popLayout" anchorX={miniPresenceAnchor(isOnLeftSide)}>
      {show && (
        <motion.div
          key="mini-quick-actions"
          variants={miniRevealVariants(isOnLeftSide)}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={MINI_REVEAL_TRANSITION}
          className={twMerge('flex items-center', !isOnLeftSide && 'flex-row-reverse')}
        >
          <MiniNewMeetingButton inPill />
          <MiniNewReminderButton />
          <MiniIncognitoButton />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
